// src/utils/employeeSchema.js - FIXED: serverTimestamp() in arrays issue
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { isEmployeeActiveToday } from "./employeeFilters";
import { matchesEmployeeSearch } from "./employeeSearch";

// Get single employee by employeeId
export const getEmployeeById = async (companyId, employeeId) => {
  try {
    if (!companyId || !employeeId) {
      throw new Error("Company ID and Employee ID are required");
    }

    const employeesRef = collection(db, "companies", companyId, "employees");
    const q = query(
      employeesRef,
      where("role", "==", "employee"),
      where("employeeId", "==", employeeId),
      where("metadata.isDeleted", "==", false)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error(`Employee ${employeeId} not found`);
    }

    const employeeDoc = querySnapshot.docs[0];
    return {
      id: employeeDoc.id,
      ...employeeDoc.data(),
    };
  } catch (error) {
    console.error("Error fetching employee by ID:", error);
    throw error;
  }
};

// Get all employees for a company
export const getEmployees = async (companyId) => {
  try {
    if (!companyId) {
      return [];
    }

    const employeesRef = collection(db, "companies", companyId, "employees");
    const q = query(
      employeesRef,
      where("role", "==", "employee"),
      where("metadata.isDeleted", "==", false)
    );

    const querySnapshot = await getDocs(q);
    const employees = [];

    querySnapshot.forEach((doc) => {
      employees.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort by employee name (client-side to avoid Firestore indexes)
    employees.sort((a, b) => {
      const nameA = `${a.personalInfo?.firstName || ""} ${
        a.personalInfo?.lastName || ""
      }`.toLowerCase();
      const nameB = `${b.personalInfo?.firstName || ""} ${
        b.personalInfo?.lastName || ""
      }`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return employees;
  } catch (error) {
    console.error("Error fetching employees:", error);
    throw error;
  }
};

// Update employee
export const updateEmployee = async (
  employeeId,
  companyId,
  updateData,
  updatedBy
) => {
  try {
    const employeesRef = collection(db, "companies", companyId, "employees");
    const q = query(
      employeesRef,
      where("role", "==", "employee"),
      where("employeeId", "==", employeeId),
      where("metadata.isDeleted", "==", false)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error(`Employee ${employeeId} not found`);
    }

    const employeeDoc = querySnapshot.docs[0];
    const currentData = employeeDoc.data();

    // Update fullName if personal info changed
    let updatedEmployee = {
      ...updateData,
      metadata: {
        ...currentData.metadata,
        version: (currentData.metadata?.version || 1) + 1,
        updatedBy,
        updatedAt: serverTimestamp(),
      },
    };

    // Update fullName and email if personalInfo changed
    if (updateData.personalInfo) {
      const firstName = updateData.personalInfo.firstName || currentData.personalInfo?.firstName || "";
      const middleName = updateData.personalInfo.middleName || currentData.personalInfo?.middleName || "";
      const lastName = updateData.personalInfo.lastName || currentData.personalInfo?.lastName || "";
      updatedEmployee.fullName = `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim();

      if (updateData.personalInfo.email) {
        updatedEmployee.email = updateData.personalInfo.email;
      }
    }

    await updateDoc(doc(db, "companies", companyId, "employees", employeeDoc.id), updatedEmployee);

    return {
      success: true,
      id: employeeDoc.id,
      employeeId,
    };
  } catch (error) {
    console.error("Error updating employee:", error);
    throw error;
  }
};

// Hard delete employee (completely removes from UI - admin only)
export const hardDeleteEmployee = async (
  employeeId,
  companyId,
  deletedBy,
  reason = "Data cleanup"
) => {
  try {
    const employeesRef = collection(db, "companies", companyId, "employees");
    const q = query(
      employeesRef,
      where("role", "==", "employee"),
      where("employeeId", "==", employeeId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error(`Employee ${employeeId} not found`);
    }

    const employeeDoc = querySnapshot.docs[0];
    const currentData = employeeDoc.data();

    const updatedEmployee = {
      ...currentData,
      metadata: {
        ...currentData.metadata,
        isDeleted: true, // NOW we hide from UI
        deletedBy,
        deletedAt: serverTimestamp(),
        deletionReason: reason,
        updatedAt: serverTimestamp(),
      },
    };

    await updateDoc(doc(db, "companies", companyId, "employees", employeeDoc.id), updatedEmployee);

    return {
      success: true,
      id: employeeDoc.id,
      employeeId,
      message: "Employee permanently deleted",
    };
  } catch (error) {
    console.error("Error hard deleting employee:", error);
    throw error;
  }
};

// Search and filter employees
export const searchEmployees = (employees, searchTerm, filters = {}) => {
  if (!employees || employees.length === 0) {
    return [];
  }

  let filteredEmployees = [...employees];

  if (searchTerm && searchTerm.trim()) {
    // The shared rule plus this page's extras — email and position (C3, C4).
    filteredEmployees = filteredEmployees.filter((employee) =>
      matchesEmployeeSearch(employee, searchTerm, [
        employee.personalInfo?.email,
        employee.employment?.position,
      ])
    );
  }

  if (filters.department && filters.department.trim()) {
    filteredEmployees = filteredEmployees.filter(
      (employee) => employee.employment?.department === filters.department
    );
  }

  if (filters.status && filters.status.trim()) {
    // Derived from the employment dates, not status.current (BL-075) — so the
    // filter agrees with the badge beside it and updates the moment an end date
    // is set or cleared, rather than at the next nightly batch run.
    filteredEmployees = filteredEmployees.filter(
      (employee) =>
        (isEmployeeActiveToday(employee) ? "active" : "inactive") === filters.status
    );
  }

  if (filters.employmentType && filters.employmentType.trim()) {
    filteredEmployees = filteredEmployees.filter(
      (employee) =>
        employee.employment?.employmentType === filters.employmentType
    );
  }

  return filteredEmployees;
};

// Export employees to CSV
export const exportEmployeesToCSV = (employees, companyName) => {
  if (!employees || employees.length === 0) {
    throw new Error("No employees to export");
  }

  const headers = [
    "Employee ID",
    "First Name",
    "Last Name",
    "Email",
    "Mobile",
    "Position",
    "Department",
    "Employment Type",
    "Status",
    "Start Date",
    "End Date",
    "Salary Type",
    "Salary Amount",
    "Bank Name",
    "Account Number",
  ];

  const rows = employees.map((employee) => [
    employee.employeeId || "",
    employee.personalInfo?.firstName || "",
    employee.personalInfo?.lastName || "",
    employee.personalInfo?.email || "",
    employee.personalInfo?.mobile || "",
    employee.employment?.position || "",
    employee.employment?.department || "",
    employee.employment?.employmentType || "",
    isEmployeeActiveToday(employee) ? "active" : "inactive",
    employee.employment?.startDate?.seconds
      ? new Date(
          employee.employment.startDate.seconds * 1000
        ).toLocaleDateString("en-PH")
      : "",
    employee.employment?.endDate?.seconds
      ? new Date(employee.employment.endDate.seconds * 1000).toLocaleDateString(
          "en-PH"
        )
      : "",
    employee.employment?.salaryType || "monthly",
    employee.employment?.salaryType === "daily"
      ? employee.employment?.dailySalary || ""
      : employee.employment?.monthlySalary || employee.employment?.basicSalary || "",
    employee.banking?.bankName || "",
    employee.banking?.accountNumber ? "***MASKED***" : "",
  ]);

  const csvContent = [headers, ...rows]
    .map((row) =>
      row.map((field) => `"${field.toString().replace(/"/g, '""')}"`).join(",")
    )
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${companyName}_employees_${new Date().toISOString().split("T")[0]}.csv`
  );
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return true;
};
